import type {
  AgencyRef,
  GetAgencyStructureRequestParams,
  GetAgencyStructureResponse,
  GetAgencyResponse,
  AddAgencyRequest,
  NewAgencyStateResponse,
  AddAgencyGroupRequest,
  NewAgencyGroupStateResponse,
  ChangeAgencyNameRequest,
  ChangeAgencyNameResponse,
  ChangeAgencyGroupNameRequest,
  ChangeAgencyGroupNameResponse,
  MoveAgencyRequest,
  MoveAgencyRequestResponse,
  RemoveAgencyGroupRequestResponse,
  ChangeAgencyContactInfoRequest,
  ChangeAgencyContactInfoRequestResponse,
  DeactivateAgencyRequest,
  DeactivateAgencyRequestResponse,
  LinkAgencyToAnotherCompanyRequest,
  LinkAgencyToAnotherCompanyRequestResponse,
  MoveAgencyGroupRequest,
  MoveAgencyGroupRequestResponse,
  ReactivateAgencyRequest,
  ActivateAgencyRequestResponse
} from '@gat-ship/types'
import { apiGet, useGsCloudBackend } from '@gat-ship/http-client'

/**
 * Standalone fetch for the flat agency reference list, used by the module-level
 * `agencyRefDataSource` singleton (mirrors `getCurrencyRefRequest` /
 * `getCountryRefsRequest`). Kept out of `useAgencyApiCalls` so it can run at
 * module scope without a component-context `inject`.
 */
export async function getAgencyRefsRequest() {
  return await apiGet<AgencyRef[]>('ref-data/organization/agencies')
}

export const AGENCY_API_PATHS = {
  agency: (agencyId: string) => `organization/agencies/${agencyId}`,
  agencyStructure: (includeInactive: boolean) =>
    `organization/agency-structure${includeInactive ? '?include_inactive=true' : ''}`,
  agencyStructureShortNameAvailability: (
    shortName: string,
    agencyNodeId?: string
  ) =>
    `organization/agency-structure/short-name-availability?short_name=${shortName}${agencyNodeId ? `&ignore_id=${agencyNodeId}` : ''}`,
  agencies: 'organization/agencies',
  agencyGroups: 'organization/agency-groups',
  agencyGroupsChangeName: (agencyGroupId: string) =>
    `organization/agency-groups/${agencyGroupId}/change-name`,
  agenciesChangeName: (agencyId: string) =>
    `organization/agencies/${agencyId}/change-name`,
  agenciesMove: (agencyId: string) => `organization/agencies/${agencyId}/move`,
  agenciesLinkAnotherCompany: (agencyId: string) =>
    `organization/agencies/${agencyId}/link-another-company`,
  agenciesChangeContactInfo: (agencyId: string) =>
    `organization/agencies/${agencyId}/change-contact-info`,
  agenciesDeactivate: (agencyId: string) =>
    `organization/agencies/${agencyId}/deactivate`,
  agenciesReactivate: (agencyId: string) =>
    `organization/agencies/${agencyId}/reactivate`,
  agencyGroupsMove: (agencyGroupId: string) =>
    `organization/agency-groups/${agencyGroupId}/move`,
  agencyGroupsRemove: (agencyGroupId: string) =>
    `organization/agency-groups/${agencyGroupId}`
}

export function useAgencyApiCalls() {
  const { apiDelete, apiGet, apiPost, apiPut } = useGsCloudBackend()
  /** Gets all organization units for the organization including the inactive ones. */
  async function getAgencyStructureRequest(
    params?: GetAgencyStructureRequestParams
  ) {
    // Defaults to `true` ("include inactive") to match the function's
    // JSDoc and the agency-structure-provider that wraps it. See the
    // companion fix in agency-structure-provider.vue.
    const result = await apiGet<GetAgencyStructureResponse>(
      AGENCY_API_PATHS.agencyStructure(params?.includeInactive ?? true)
    )
    return result?.nodes
  }

  /** Gets all organization units for the organization including the inactive ones. */
  async function getAgencyRequest(agencyId: string) {
    const response = await apiGet<GetAgencyResponse>(
      AGENCY_API_PATHS.agency(agencyId)
    )
    return response
  }

  async function addAgencyRequest(
    payload: AddAgencyRequest,
    commandId: string
  ) {
    const response = await apiPost<NewAgencyStateResponse, AddAgencyRequest>(
      AGENCY_API_PATHS.agencies,
      payload,
      commandId
    )
    return response
  }

  async function addAgencyGroupRequest(
    payload: AddAgencyGroupRequest,
    commandId: string
  ) {
    const response = await apiPost<
      NewAgencyGroupStateResponse,
      AddAgencyGroupRequest
    >(AGENCY_API_PATHS.agencyGroups, payload, commandId)
    return response
  }

  /** Represent a command that can be used to change the name of an agency. */
  async function changeAgencyNameRequest(
    agencyId: string,
    payload: ChangeAgencyNameRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ChangeAgencyNameResponse,
      ChangeAgencyNameRequest
    >(AGENCY_API_PATHS.agenciesChangeName(agencyId), payload, commandId)
    return response
  }

  /** Represent a command that can be used to change the name of an agency. */
  async function changeAgencyGroupNameRequest(
    agencyGroupId: string,
    payload: ChangeAgencyGroupNameRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ChangeAgencyGroupNameResponse,
      ChangeAgencyGroupNameRequest
    >(
      AGENCY_API_PATHS.agencyGroupsChangeName(agencyGroupId),
      payload,
      commandId
    )
    return response
  }

  async function moveAgencyRequest(
    agencyId: string,
    payload: MoveAgencyRequest,
    commandId: string
  ) {
    const response = await apiPut<MoveAgencyRequestResponse, MoveAgencyRequest>(
      AGENCY_API_PATHS.agenciesMove(agencyId),
      payload,
      commandId
    )
    return response
  }

  /** Represent a command that can be used to move an agency group under another group. */
  async function moveAgencyGroupRequest(
    agencyGroupId: string,
    payload: MoveAgencyGroupRequest,
    commandId: string
  ) {
    const response = await apiPut<
      MoveAgencyGroupRequestResponse,
      MoveAgencyGroupRequest
    >(AGENCY_API_PATHS.agencyGroupsMove(agencyGroupId), payload, commandId)
    return response
  }

  /** Represent a command that can be used to link another company to the agency. */
  async function linkAgencyToAnotherCompanyRequest(
    agencyId: string,
    payload: LinkAgencyToAnotherCompanyRequest,
    commandId: string
  ) {
    const response = await apiPut<
      LinkAgencyToAnotherCompanyRequestResponse,
      LinkAgencyToAnotherCompanyRequest
    >(AGENCY_API_PATHS.agenciesLinkAnotherCompany(agencyId), payload, commandId)
    return response
  }

  /** Represent a command that can be used to change or update the contact info for an agency. */
  async function changeAgencyContactInfoRequest(
    agencyId: string,
    payload: ChangeAgencyContactInfoRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ChangeAgencyContactInfoRequestResponse,
      ChangeAgencyContactInfoRequest
    >(AGENCY_API_PATHS.agenciesChangeContactInfo(agencyId), payload, commandId)
    return response
  }

  /** Verifies whether a short name is available and does not already exist.
   * @param shortName The short name value to check.
   * @param agencyNodeId The ID of the agency or group to ignore in the check.
   */
  async function checkShortNameAvailabilityRequest(
    shortName: string,
    agencyNodeId?: string
  ) {
    const response = await apiGet<boolean>(
      AGENCY_API_PATHS.agencyStructureShortNameAvailability(
        shortName,
        agencyNodeId
      )
    )
    return response
  }

  /** Represent a command that can be used to deactivate an agency. */
  async function deactivateAgencyRequest(
    agencyId: string,
    payload: DeactivateAgencyRequest,
    commandId: string
  ) {
    const response = await apiPut<
      DeactivateAgencyRequestResponse,
      DeactivateAgencyRequest
    >(AGENCY_API_PATHS.agenciesDeactivate(agencyId), payload, commandId)
    return response
  }

  /** Represent a command that can be used to deactivate an agency. */
  async function reactivateAgencyRequest(
    agencyId: string,
    payload: ReactivateAgencyRequest,
    commandId: string
  ) {
    const response = await apiPut<
      ActivateAgencyRequestResponse,
      ReactivateAgencyRequest
    >(AGENCY_API_PATHS.agenciesReactivate(agencyId), payload, commandId)
    return response
  }

  async function removeAgencyGroupRequest(
    agencyGroupId: string,
    commandId: string
  ) {
    const response = await apiDelete<
      RemoveAgencyGroupRequestResponse,
      undefined
    >(AGENCY_API_PATHS.agencyGroupsRemove(agencyGroupId), undefined, commandId)
    return response
  }

  return {
    getAgencyStructureRequest,
    getAgencyRequest,
    addAgencyRequest,
    addAgencyGroupRequest,
    changeAgencyNameRequest,
    changeAgencyGroupNameRequest,
    moveAgencyRequest,
    moveAgencyGroupRequest,
    linkAgencyToAnotherCompanyRequest,
    changeAgencyContactInfoRequest,
    checkShortNameAvailabilityRequest,
    deactivateAgencyRequest,
    reactivateAgencyRequest,
    removeAgencyGroupRequest
  }
}
